import { getDefaultTeamImage, MAX_TEAMS, MIN_TEAMS } from './constants'
import type { Match, Seed, SelectionMap, Slot, SlotStatus } from './types'

export function clampTeams(value: number) {
  if (Number.isNaN(value)) return MIN_TEAMS
  return Math.max(MIN_TEAMS, Math.min(MAX_TEAMS, Math.floor(value)))
}

export function nextPowerOfTwo(n: number) {
  return 2 ** Math.ceil(Math.log2(Math.max(n, MIN_TEAMS)))
}

export function createSample(count: number) {
  return Array.from({ length: count }, (_, i) => `Team ${i + 1}`)
}

export function createPlaceholderImages(count: number) {
  return Array.from({ length: count }, (_, index) => getDefaultTeamImage(index))
}

type PositionedMatch = {
  span: number
  startRow: number
  centerY: number
  columnIndex: number
  relativeColumn: number
  translateY: number
}

type BracketLayout = {
  matchLayout: PositionedMatch[][]
  columnCount: number
  centerColumn: number
  maxRegionOffset: number
}
type BracketDimensions = {
  bracketHeight: number
  bracketWidth: number
  championColumnIndex: number | null
  columnOffsets: number[]
  columnWidths: number[]
  finalColumnIndex: number
  finalColumnWidth: number
  hasChampionColumn: boolean
}
type RenderedBracketDimensions = {
  bracketHeight: number
  bracketWidth: number
  verticalContentWidth: number
  verticalXOffset: number
}
type BracketBox = {
  height: number
  left: number
  top: number
  width: number
}

export function buildSeeds(
  participants: string[],
  participantImages: (string | null)[],
  normalizedTeamCount: number,
  totalSlots: number,
): Seed[] {
  const names = participants.slice(0, normalizedTeamCount)
  const images = participantImages.slice(0, normalizedTeamCount)

  while (names.length < normalizedTeamCount) names.push('')
  while (images.length < normalizedTeamCount)
    images.push(getDefaultTeamImage(images.length))
  while (names.length < totalSlots) names.push('')
  while (images.length < totalSlots) images.push(null)

  return names.slice(0, totalSlots).map((name, index) => {
    const image = images[index] ?? null
    const hasName = name.trim().length > 0
    return {
      name,
      image: hasName ? image : null,
    }
  })
}

export function buildRounds(
  seeds: Seed[],
  selections: SelectionMap,
): Match[][] {
  let current: Slot[] = seeds.map((seed) => ({
    name: seed.name,
    image: seed.image,
    status: seed.name.trim().length > 0 ? 'team' : 'bye',
  }))
  const rounds: Match[][] = []
  const roundCount = Math.log2(seeds.length)

  for (let roundIndex = 0; roundIndex < roundCount; roundIndex++) {
    const matches: Match[] = []
    const next: Slot[] = []

    for (let i = 0; i < current.length; i += 2) {
      const homeSlot = current[i] ?? { name: '', status: 'bye' }
      const awaySlot = current[i + 1] ?? { name: '', status: 'bye' }
      const home = homeSlot.name
      const away = awaySlot.name
      const homeImage = homeSlot.image
      const awayImage = awaySlot.image
      const selection = selections[roundIndex]?.[i / 2]
      const hasHome = homeSlot.status === 'team'
      const hasAway = awaySlot.status === 'team'
      const homeIsBye = homeSlot.status === 'bye'
      const awayIsBye = awaySlot.status === 'bye'
      let winner = ''
      let winnerImage: string | null | undefined = null
      let auto = false
      if (selection === 'home') {
        winner = home
        winnerImage = homeImage
      } else if (selection === 'away') {
        winner = away
        winnerImage = awayImage
      } else if (hasHome && awayIsBye) {
        winner = home
        winnerImage = homeImage
        auto = true
      } else if (hasAway && homeIsBye) {
        winner = away
        winnerImage = awayImage
        auto = true
      }

      matches.push({
        home,
        away,
        homeImage,
        awayImage,
        winner,
        winnerImage,
        auto,
        selection,
      })
      const hasDecision = winner.trim().length > 0
      const nextStatus: SlotStatus = hasDecision
        ? 'team'
        : homeIsBye && awayIsBye
          ? 'bye'
          : 'pending'

      next.push({
        name: winner,
        image: winnerImage,
        status: nextStatus,
      })
    }

    rounds.push(matches)
    current = next
  }

  return rounds
}

export function clearDownstreamSelections(
  currentSelections: SelectionMap,
  roundIndex: number,
  matchIndex: number,
  totalRounds: number,
) {
  const next = { ...currentSelections }
  let downstreamIndex = Math.floor(matchIndex / 2)

  for (let r = roundIndex + 1; r < totalRounds; r++) {
    if (next[r]) {
      const { [downstreamIndex]: _removed, ...rest } = next[r]
      if (Object.keys(rest).length === 0) {
        delete next[r]
      } else {
        next[r] = rest
      }
    }
    downstreamIndex = Math.floor(downstreamIndex / 2)
  }

  return next
}

export function updateSelectionsAfterSeedRename(
  selections: SelectionMap,
  previousName: string,
  nextName: string,
  seedIndex: number,
  totalRounds: number,
) {
  // 普通改名不改变队伍身份，保留按主客位置记录的晋级结果。
  if (Boolean(previousName.trim()) === Boolean(nextName.trim())) {
    return selections
  }

  // 队伍与轮空互换时，从种子层（第 -1 轮）清除首场及其晋级路径。
  return clearDownstreamSelections(selections, -1, seedIndex, totalRounds)
}

// 只打乱种子索引，保持原始名单与图片的对应关系，重复队名也不会混淆。
export function shuffleSeedOrder(
  count: number,
  random = Math.random,
): number[] {
  const order = Array.from({ length: count }, (_, index) => index)
  for (let index = order.length - 1; index > 0; index--) {
    const target = Math.floor(random() * (index + 1))
    ;[order[index], order[target]] = [order[target], order[index]]
  }
  return order
}

export function reorderSeeds(seeds: Seed[], order: number[] | null): Seed[] {
  if (!order) return seeds
  return [...order.map((index) => seeds[index]), ...seeds.slice(order.length)]
}
